/**
 * Phase 4 client-facing React Query hook family. Every portal page imports
 * its data layer from here; no page should talk to apiFetch directly for
 * client endpoints.
 *
 * Conventions:
 *   - Query keys: ['client', <resource>, ...params]
 *   - Types flow from @ventin/shared — no duplicated shape definitions
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { apiFetch } from './api';
import type {
  DashboardUsagePayload,
  MonthlyReportSummary,
  ClientCompanyWebsiteSummary,
} from '@ventin/shared';

// ---------- query key roots ----------

const KEYS = {
  dashboard: ['client', 'dashboard'] as const,
  reports: ['client', 'reports'] as const,
  report: (id: string) => ['client', 'reports', id] as const,
  websites: ['client', 'websites'] as const,
} as const;

// ---------- qs helper (mirrors adminApi.ts convention) ----------

function qs(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (typeof v === 'boolean') {
      if (v) usp.set(k, '1');
      continue;
    }
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ============================================================
// DASHBOARD
// ============================================================

export function useDashboardUsage(
  opts: { refetchInterval?: number } = {},
  options?: Omit<
    UseQueryOptions<DashboardUsagePayload>,
    'queryKey' | 'queryFn' | 'refetchInterval'
  >,
) {
  return useQuery({
    queryKey: KEYS.dashboard,
    queryFn: () => apiFetch<DashboardUsagePayload>('/api/client/dashboard/usage'),
    refetchInterval: opts.refetchInterval,
    ...options,
  });
}

// ============================================================
// REPORTS
// ============================================================

export interface ReportsListResponse {
  reports: MonthlyReportSummary[];
  nextCursor: string | null;
}

export interface ReportsListFilter {
  limit?: number;
  cursor?: string;
}

export function useReports(
  filter: ReportsListFilter = {},
  options?: Omit<UseQueryOptions<ReportsListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...KEYS.reports, filter] as const,
    queryFn: () =>
      apiFetch<ReportsListResponse>(`/api/client/reports${qs(filter as Record<string, unknown>)}`),
    ...options,
  });
}

/**
 * Build the download URL used by the Reports page download buttons.
 * The page triggers navigation (window.location = url); the server streams
 * the stored report file with Content-Disposition: attachment.
 *
 * Note: this is NOT a hook — it is a pure URL builder. Plan 16 uses it
 * directly in onClick handlers.
 */
export function buildReportDownloadUrl(id: string, format: 'pdf' | 'csv'): string {
  return `/api/client/reports/${encodeURIComponent(id)}/download?format=${format}`;
}

// ============================================================
// WEBSITES
// ============================================================

/**
 * The caller's company websites (session-scoped). Used by the portal
 * new-ticket form to let a client attribute a ticket to a specific site when
 * their company has more than one (plan §4.4 / §2.5).
 */
export function useClientWebsites(
  options?: Omit<
    UseQueryOptions<{ websites: ClientCompanyWebsiteSummary[] }>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: KEYS.websites,
    queryFn: () => apiFetch<{ websites: ClientCompanyWebsiteSummary[] }>('/api/client/websites'),
    ...options,
  });
}
